import { useState } from 'react'
import { useStore } from '../store/store'
import { decimalToImplied, expectedValue, recommendedStake } from '../lib/math'
import { redTeamIdea } from '../lib/behavior'
import { fmtCurrency, fmtPct, fmtSigned } from '../lib/format'
import { Button, Card, NumberField, PageHead, Pill, type Tone } from '../components/ui'
import { ActionPill, RedTeamPanel } from '../components/widgets'

const decisionHint: Record<string, { tone: Tone; text: string }> = {
  strong: { tone: 'green', text: 'Clear positive EV beyond your threshold. Size conservatively.' },
  small: { tone: 'blue', text: 'Real but modest edge. Small position only.' },
  paper: { tone: 'amber', text: 'Edge is thin. Paper-trade until confirmed.' },
  skip: { tone: 'red', text: 'No edge after costs. This is a No-Bet.' },
}

export function EvCalculator() {
  const risk = useStore((s) => s.risk)
  const [modelProb, setModelProb] = useState(0.55)
  const [decimalOdds, setDecimalOdds] = useState(2)
  const [feePct, setFeePct] = useState(0)
  const [spreadPct, setSpreadPct] = useState(0)
  const [slippagePct, setSlippagePct] = useState(0)
  const [stake, setStake] = useState(100)
  const [showRedTeam, setShowRedTeam] = useState(false)

  const ev = expectedValue({ modelProb, decimalOdds, stake, feePct, spreadPct, slippagePct, minEdge: risk.minEdge })
  const marketProb = decimalToImplied(decimalOdds)
  const hint = decisionHint[ev.decision]

  const stakes = (['full', 'half', 'quarter'] as const).map((mode) => ({
    mode,
    result: recommendedStake({ bankroll: risk.bankroll, prob: modelProb, decimalOdds, mode, maxStakePct: risk.maxSinglePositionPct }),
  }))

  return (
    <div className="stack">
      <PageHead title="Expected Value Calculator">
        EV is the whole game. Enter your probability, the price, and every cost. If it isn't positive after fees and spread, it's a skip.
      </PageHead>

      <div className="grid cols-2">
        <Card title="Inputs">
          <div className="grid cols-2">
            <NumberField label="Your probability" value={modelProb} onChange={setModelProb} step={0.01} min={0} max={1} hint="0–1" />
            <NumberField label="Decimal odds" value={decimalOdds} onChange={setDecimalOdds} step={0.01} min={1.01} />
            <NumberField label="Fee % (on winnings)" value={feePct} onChange={setFeePct} step={0.01} min={0} max={0.5} />
            <NumberField label="Spread %" value={spreadPct} onChange={setSpreadPct} step={0.005} min={0} max={0.5} />
            <NumberField label="Slippage %" value={slippagePct} onChange={setSlippagePct} step={0.005} min={0} max={0.5} />
            <NumberField label="Stake" value={stake} onChange={setStake} step={10} min={0} />
          </div>
          <Button onClick={() => setShowRedTeam((v) => !v)}>{'\uD83D\uDD25'} Red Team my idea</Button>
        </Card>

        <Card title="Verdict" action={<ActionPill action={ev.decision} />}>
          <div className="stat-value" style={{ marginBottom: 6 }}>
            <span className={ev.ev >= 0 ? 'pos' : 'neg'}>{fmtSigned(ev.ev, 2)}</span> <span className="muted" style={{ fontSize: 13 }}>EV on {fmtCurrency(stake)} stake</span>
          </div>
          <div className="kv"><span className="k">EV per $1 staked</span><span className={`v ${ev.evPerUnit >= 0 ? 'pos' : 'neg'}`}>{fmtSigned(ev.evPerUnit, 4)}</span></div>
          <div className="kv"><span className="k">Your edge</span><span className={`v ${ev.edge >= 0 ? 'pos' : 'neg'}`}>{fmtSigned(ev.edge * 100, 2)}%</span></div>
          <div className="kv"><span className="k">Market implied prob</span><span className="v">{fmtPct(marketProb, 2)}</span></div>
          <div className="kv"><span className="k">Break-even prob (after fees)</span><span className="v">{fmtPct(ev.breakEvenProb, 2)}</span></div>
          <div className="kv"><span className="k">Payout if win</span><span className="v pos">{fmtCurrency(ev.payoutIfWin * stake)}</span></div>
          <div className="kv"><span className="k">Loss if lose</span><span className="v neg">−{fmtCurrency(stake)}</span></div>
          <div style={{ marginTop: 10 }}>
            <Pill tone={hint.tone}>{hint.text}</Pill>
          </div>
        </Card>
      </div>

      {showRedTeam && (
        <RedTeamPanel
          onClose={() => setShowRedTeam(false)}
          args={redTeamIdea({ modelProb, marketProb, edge: ev.edge, dataQuality: 0.6, confidence: 0.6, liquidity: 0.7 })}
        />
      )}

      <Card title="Kelly staking" subtitle={`From a ${fmtCurrency(risk.bankroll)} bankroll, capped at ${fmtPct(risk.maxSinglePositionPct, 0)} per position. Fractional Kelly is strongly recommended.`}>
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr><th>Mode</th><th>Fraction of bankroll</th><th>Stake</th><th>Status</th></tr>
            </thead>
            <tbody>
              {stakes.map(({ mode, result }) => (
                <tr key={mode}>
                  <td style={{ textTransform: 'capitalize' }}>{mode} Kelly</td>
                  <td className="mono">{fmtPct(result.fraction, 2)}</td>
                  <td className="mono">{fmtCurrency(result.stake)}</td>
                  <td>{result.edgePresent ? <Pill tone={result.cappedBy === 'pct-cap' ? 'amber' : 'green'}>{result.cappedBy === 'pct-cap' ? 'Capped' : 'OK'}</Pill> : <Pill tone="red">No edge</Pill>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="help" style={{ marginTop: 8 }}>
          Full Kelly maximizes long-run growth but is brutally volatile. Most disciplined bettors use quarter-Kelly or less. EdgeLab defaults to fractional Kelly with hard caps.
        </p>
      </Card>
    </div>
  )
}
